import { TierEnum } from '@/Domain/enum/Tier.enum';

export type FeatureComparisonPlanKey =
  | 'free'
  | 'starter'
  | 'basic'
  | 'essential'
  | 'professional'
  | 'business'
  | 'custom';

export type FeatureComparisonFeatureValue =
  | { type: 'check' }
  | { type: 'dash' }
  | { type: 'text'; text: string }
  | { type: 'translation'; translationKey: string };

export type FeatureComparisonFeature = {
  key: string;
  translationKey: string;
  tooltipKey?: string;
  values: Record<FeatureComparisonPlanKey, FeatureComparisonFeatureValue>;
};

export type FeatureComparisonCategory = {
  key: string;
  translationKey: string;
  features: FeatureComparisonFeature[];
};

export type FeatureComparisonPlanConfig = {
  key: FeatureComparisonPlanKey;
  tier: TierEnum | null;
  productKey:
    | 'starter'
    | 'basic_2026'
    | 'essential_2026'
    | 'professional_2026'
    | 'business_2026'
    | null;
  translationKey: string;
  badgeTranslationKey?: string;
  isHighlighted?: boolean;
  isCustom?: boolean;
  ctaTranslationKey: string;
  price: {
    monthly: string;
    annually: string;
    translationMonthlyKey?: string;
    translationAnnuallyKey?: string;
  };
  subheaderTranslationKey?: string;
};

export const FEATURE_COMPARISON_PLANS: FeatureComparisonPlanConfig[] = [
  {
    key: 'free',
    tier: TierEnum.FREE,
    productKey: null,
    translationKey: 'subscription_page:tiers.free',
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.free.cta',
    price: {
      monthly: '$0',
      annually: '$0',
    },
  },
  {
    key: 'starter',
    tier: TierEnum.STARTER,
    productKey: 'starter',
    translationKey: 'subscription_page:tiers.starter',
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.starter.cta',
    price: {
      monthly: '$9',
      annually: '$8',
    },
  },
  {
    key: 'basic',
    tier: TierEnum.BASIC_2026,
    productKey: 'basic_2026',
    translationKey: 'subscription_page:tiers.basic',
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.basic.cta',
    price: {
      monthly: '$29',
      annually: '$24',
    },
  },
  {
    key: 'essential',
    tier: TierEnum.ESSENTIAL_2026,
    productKey: 'essential_2026',
    translationKey: 'subscription_page:tiers.essential',
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.essential.cta',
    price: {
      monthly: '$99',
      annually: '$83',
    },
  },
  {
    key: 'professional',
    tier: TierEnum.PROFESSIONAL_2026,
    productKey: 'professional_2026',
    translationKey: 'subscription_page:tiers.professional',
    badgeTranslationKey: 'subscription_page:feature_comparison.popular_badge',
    isHighlighted: true,
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.professional.cta',
    price: {
      monthly: '$299',
      annually: '$249',
    },
  },
  {
    key: 'business',
    tier: TierEnum.BUSINESS_2026,
    productKey: 'business_2026',
    translationKey: 'subscription_page:tiers.business',
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.business.cta',
    price: {
      monthly: '$999',
      annually: '$833',
    },
  },
  {
    key: 'custom',
    tier: TierEnum.CUSTOM,
    productKey: null,
    translationKey: 'subscription_page:tiers.custom',
    isCustom: true,
    subheaderTranslationKey: 'subscription_page:plan_great_for.custom',
    ctaTranslationKey: 'subscription_page:feature_comparison.plans.custom.cta',
    price: {
      monthly: '$0.50',
      annually: '$0.50',
      translationMonthlyKey: 'subscription_page:feature_comparison.plans.custom.price_monthly',
      translationAnnuallyKey: 'subscription_page:feature_comparison.plans.custom.price_annually',
    },
  },
];

export const FEATURE_COMPARISON_CATEGORIES: FeatureComparisonCategory[] = [
  {
    key: 'general',
    translationKey: 'subscription_page:feature_comparison.categories.general.name',
    features: [
      {
        key: 'messages_per_month',
        translationKey:
          'subscription_page:feature_comparison.categories.general.features.messages_per_month.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.general.features.messages_per_month.tooltip',
        values: {
          free: { type: 'text', text: '50' },
          starter: { type: 'text', text: '150' },
          basic: { type: 'text', text: '500' },
          essential: { type: 'text', text: '3,000' },
          professional: { type: 'text', text: '9,000' },
          business: { type: 'text', text: '30,000' },
          custom: {
            type: 'translation',
            translationKey:
              'subscription_page:feature_comparison.categories.general.features.messages_per_month.values.unlimited',
          },
        },
      },
      {
        key: 'go_live',
        translationKey:
          'subscription_page:feature_comparison.categories.general.features.go_live.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.general.features.go_live.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        // Displayed values are `collaborators + 1` to include the account
        // owner, who isn't counted as a collaborator on the backend
        // (`pricing_plans.yaml`) but does occupy a user seat.
        key: 'users',
        translationKey:
          'subscription_page:feature_comparison.categories.general.features.users.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.general.features.users.tooltip',
        values: {
          free: { type: 'text', text: '1' },
          starter: { type: 'text', text: '1' },
          basic: { type: 'text', text: '1' },
          essential: { type: 'text', text: '1' },
          professional: { type: 'text', text: '3' },
          business: { type: 'text', text: '10' },
          custom: {
            type: 'translation',
            translationKey:
              'subscription_page:feature_comparison.categories.general.features.users.values.unlimited',
          },
        },
      },
      {
        key: 'user_roles',
        translationKey:
          'subscription_page:feature_comparison.categories.general.features.user_roles.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.general.features.user_roles.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
    ],
  },
  {
    key: 'knowledge',
    translationKey: 'subscription_page:feature_comparison.categories.knowledge.name',
    features: [
      {
        key: 'articles_limit',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.articles_limit.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.articles_limit.tooltip',
        values: {
          free: { type: 'text', text: '50' },
          starter: { type: 'text', text: '100' },
          basic: { type: 'text', text: '300' },
          essential: { type: 'text', text: '500' },
          professional: { type: 'text', text: '1,000' },
          business: { type: 'text', text: '2,000' },
          custom: {
            type: 'translation',
            translationKey:
              'subscription_page:feature_comparison.categories.knowledge.features.articles_limit.unlimited',
          },
        },
      },
      {
        key: 'text_input',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.text_input.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.text_input.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'website_crawling',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.website_crawling.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.website_crawling.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'auto_refresh_url',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.auto_refresh_url.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.auto_refresh_url.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'files',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.files.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.files.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'shopify',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.shopify.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.shopify.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'knowledge_sync',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.knowledge_sync.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.knowledge_sync.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'custom_api',
        translationKey:
          'subscription_page:feature_comparison.categories.knowledge.features.custom_api.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.knowledge.features.custom_api.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'dash' },
          basic: { type: 'dash' },
          essential: { type: 'dash' },
          professional: { type: 'dash' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
    ],
  },
  {
    key: 'ai_intelligence',
    translationKey: 'subscription_page:feature_comparison.categories.ai_intelligence.name',
    features: [
      {
        key: 'multilingual',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.multilingual.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.multilingual.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'ai_guidelines',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.ai_guidelines.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.ai_guidelines.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'advanced_models',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.advanced_models.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.advanced_models.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'premium_models',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.premium_models.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.premium_models.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'personality',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.personality.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.personality.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'profession',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.profession.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.profession.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'creativity',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.creativity.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.creativity.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'reply_length',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.reply_length.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.reply_length.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'api_actions',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.api_actions.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.api_actions.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'active_actions',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.active_actions.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.active_actions.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: {
            type: 'translation',
            translationKey:
              'subscription_page:feature_comparison.categories.ai_intelligence.features.active_actions.values.unlimited',
          },
        },
      },
      {
        key: 'human_handoff',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.human_handoff.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.human_handoff.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'availability_hours',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.availability_hours.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.availability_hours.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'lead_generation',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.lead_generation.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.lead_generation.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'export_conversations',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.export_conversations.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.export_conversations.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'dash' },
          basic: { type: 'dash' },
          essential: { type: 'dash' },
          professional: { type: 'dash' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'custom_personality',
        translationKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.custom_personality.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.ai_intelligence.features.custom_personality.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
    ],
  },
  {
    key: 'channels',
    translationKey: 'subscription_page:feature_comparison.categories.channels.name',
    features: [
      {
        key: 'public_url',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.public_url.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.public_url.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'chat_widget',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.chat_widget.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.chat_widget.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'embed_iframe',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.embed_iframe.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.embed_iframe.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'customizable_branding',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.customizable_branding.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.customizable_branding.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'remove_branding',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.remove_branding.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.remove_branding.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'dash' },
          basic: { type: 'dash' },
          essential: { type: 'dash' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'voice_input',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.voice_input.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.voice_input.tooltip',
        // Mirrors FeatureBundle.voice_input: every paid tier, never Free or Trial.
        values: {
          free: { type: 'dash' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'discord',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.discord.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.discord.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'whatsapp',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.whatsapp.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.whatsapp.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'messenger',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.messenger.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.messenger.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'slack',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.slack.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.slack.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'telegram',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.telegram.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.telegram.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'intercom',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.intercom.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.intercom.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'hubspot',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.hubspot.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.hubspot.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'zendesk',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.zendesk.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.zendesk.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'drupal',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.drupal.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.drupal.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'api_access',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.api_access.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.api_access.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'dash' },
          basic: { type: 'dash' },
          essential: { type: 'dash' },
          professional: { type: 'dash' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'custom_api',
        translationKey:
          'subscription_page:feature_comparison.categories.channels.features.custom_api.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.channels.features.custom_api.tooltip',
        values: {
          free: { type: 'dash' },
          starter: { type: 'dash' },
          basic: { type: 'dash' },
          essential: { type: 'dash' },
          professional: { type: 'dash' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
    ],
  },
  {
    key: 'analytics',
    translationKey: 'subscription_page:feature_comparison.categories.analytics.name',
    features: [
      {
        key: 'sentiment_analysis',
        translationKey:
          'subscription_page:feature_comparison.categories.analytics.features.sentiment_analysis.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.analytics.features.sentiment_analysis.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'link_tracking',
        translationKey:
          'subscription_page:feature_comparison.categories.analytics.features.link_tracking.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.analytics.features.link_tracking.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'conversation_rating',
        translationKey:
          'subscription_page:feature_comparison.categories.analytics.features.conversation_rating.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.analytics.features.conversation_rating.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'outcome_prediction',
        translationKey:
          'subscription_page:feature_comparison.categories.analytics.features.outcome_prediction.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.analytics.features.outcome_prediction.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'topic_detection',
        translationKey:
          'subscription_page:feature_comparison.categories.analytics.features.topic_detection.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.analytics.features.topic_detection.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
    ],
  },
  {
    key: 'inbox',
    translationKey: 'subscription_page:feature_comparison.categories.inbox.name',
    features: [
      {
        key: 'conversation_attributes',
        translationKey:
          'subscription_page:feature_comparison.categories.inbox.features.conversation_attributes.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.inbox.features.conversation_attributes.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'visitor_attributes',
        translationKey:
          'subscription_page:feature_comparison.categories.inbox.features.visitor_attributes.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.inbox.features.visitor_attributes.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'assign_close',
        translationKey:
          'subscription_page:feature_comparison.categories.inbox.features.assign_close.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.inbox.features.assign_close.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'response_analysis',
        translationKey:
          'subscription_page:feature_comparison.categories.inbox.features.response_analysis.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.inbox.features.response_analysis.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'ai_summary',
        translationKey:
          'subscription_page:feature_comparison.categories.inbox.features.ai_summary.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.inbox.features.ai_summary.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
      {
        key: 'message_translation',
        translationKey:
          'subscription_page:feature_comparison.categories.inbox.features.message_translation.name',
        tooltipKey:
          'subscription_page:feature_comparison.categories.inbox.features.message_translation.tooltip',
        values: {
          free: { type: 'check' },
          starter: { type: 'check' },
          basic: { type: 'check' },
          essential: { type: 'check' },
          professional: { type: 'check' },
          business: { type: 'check' },
          custom: { type: 'check' },
        },
      },
    ],
  },
];
